// src/components/Footer.jsx
import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { db } from "../firebase"; // ✅ your initialized Firestore
import { collection, addDoc, Timestamp } from "firebase/firestore";

import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Facebook,
  Twitter,
  Instagram,
  ExternalLink,
  Send,
} from "lucide-react";

import { useToast } from "../hooks/use-toast";
import { Link, useLocation } from "react-router-dom";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const { toast } = useToast();
  const location = useLocation();

  // ---------------------------
  // Handle newsletter subscription
  // ---------------------------
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubscribing(true);

    try {
      // Add email to Firestore
      await addDoc(collection(db, "newsletter_subscribers"), {
        email: email,
        subscribedAt: Timestamp.now(),
      });

      toast({
        title: "Successfully Subscribed!",
        description: "Thank you for subscribing to our newsletter.",
      });

      setEmail(""); // Clear input
    } catch (error) {
      console.error("Error subscribing:", error);
      toast({
        title: "Error",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubscribing(false);
    }
  };

  // ---------------------------
  // Smooth scroll for hash links
  // ---------------------------
  useEffect(() => {
    if (location.hash) {
      const section = document.querySelector(location.hash);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  const quickLinks = [
    { name: "Home", href: "/#home" },
    { name: "About", href: "/#about" },
    { name: "Services", href: "/#services" },
    { name: "Team", href: "/#team" },
    { name: "Locations", href: "/#branches" },
    { name: "Contact", href: "/#contact" },
  ];

  const services = [
    { name: "Sports Injury Recovery", href: "/#services" },
    { name: "Pain Management", href: "/#services" },
    { name: "Chiropractic Care", href: "/#services" },
    { name: "Rehabilitation", href: "/#services" },
    { name: "Wellness Programs", href: "/#services" },
    { name: "Corporate Wellness", href: "/#services" },
  ];

  const socialLinks = [
  { icon: Facebook, name: "Facebook", href: "https://www.facebook.com/jasti_physiochiro" },
  { icon: Twitter, name: "Twitter", href: "https://x.com/j_physiochiro" },
  { icon: Instagram, name: "Instagram", href: "https://www.instagram.com/jasti_physiochiro/" },
  { icon: ExternalLink, name: "LinkedIn", href: "https://www.linkedin.com/company/visakha-physiotherapy-jasti-chiropractic-vpjc/" },
];


  return (
    <footer className="bg-gradient-to-br from-[#204080] to-[#2E5AAC] text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-white rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
      </div>

      <div className="relative">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-12">
            {/* Company Info */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h3 className="text-3xl font-bold mb-4">Visakha Physiotherapy</h3>
                <p className="text-blue-100 text-lg leading-relaxed">
                  For over 22 years, we've been dedicated to restoring movement,
                  relieving pain, and uplifting lives through expert physiotherapy and wellness care.
                </p>
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Phone size={18} className="text-blue-200" />
                  <a href="tel:+919281010745" className="text-blue-100 hover:underline">
                    +91 92810 10745
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail size={18} className="text-blue-200" />
                  <a href="mailto:contact@jastiphysiochiro.com" className="text-blue-100 hover:underline">
                    contact@jastiphysiochiro.com
                  </a>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin size={18} className="text-blue-200 mt-1 flex-shrink-0" />
                  <div className="text-blue-100">
                    <div>
                      <span className="font-bold">Gajuwaka: </span>
                      <span>Chinagantyada Junction, Kanithi Road, Gajuwaka</span>
                    </div>
                    <div>
                      <span className="font-bold">Kurmannpalem: </span>
                      <span>Duvvada Rd, Kurmannapalem, Visakhapatnam</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock size={18} className="text-blue-200" />
                  <span className="text-blue-100">
                    Mon-Sat: 09:00 AM - 09:00 PM
                    <br />
                    Sun: 09:00 AM to 05:00 PM
                  </span>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex items-center space-x-4">
                <span className="text-blue-200 text-sm">Follow Us:</span>
                <div className="flex space-x-3">
                  {socialLinks.map((social, index) => {
                    const IconComponent = social.icon;
                    return (
                      <a
                        key={index}
                        href={social.href}
                        className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors duration-300"
                        aria-label={social.name}
                      >
                        <IconComponent size={18} />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="space-y-6">
              <h4 className="text-xl font-bold">Quick Links</h4>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.href}
                      className="text-blue-100 hover:text-white transition-colors duration-300 hover:underline"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div className="space-y-6">
              <h4 className="text-xl font-bold">Our Services</h4>
              <ul className="space-y-3">
                {services.map((service, index) => (
                  <li key={index}>
                    <Link
                      to={service.href}
                      className="text-blue-100 hover:text-white transition-colors duration-300 hover:underline"
                    >
                      {service.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Newsletter Section */}
          <div className="mt-16 pt-12 border-t border-white/20">
            <div className="max-w-2xl mx-auto text-center space-y-6">
              <h4 className="text-2xl font-bold">Stay Updated with Health Tips & News</h4>
              <p className="text-blue-100 text-lg">
                Subscribe to our newsletter for expert health advice, wellness tips, and clinic updates.
              </p>

              <form
                onSubmit={handleNewsletterSubmit}
                className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
              >
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  className="flex-1 bg-white/10 border-white/30 text-white placeholder-blue-200 focus:bg-white/20 focus:border-white"
                />
                <Button
                  type="submit"
                  disabled={isSubscribing}
                  className="bg-white text-[#204080] hover:bg-blue-50 px-6 py-2 font-semibold transition-all duration-300"
                >
                  {isSubscribing ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#204080] border-t-transparent"></div>
                      <span>Subscribing...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Send size={16} />
                      <span>Subscribe</span>
                    </div>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 bg-black/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-blue-200 text-sm">© 2025 Visakha Physiotherapy. All rights reserved.</div>
              <div className="text-sm text-blue-200">
                Powered By{" "}
                <a
                  href="https://www.charvionglobal.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors duration-300"
                >
                  Charvion Global Pvt. Ltd.
                </a>
              </div>
              <div className="flex items-center space-x-6 text-sm text-blue-200">
  <Link to="/privacy-policy" className="hover:text-white transition-colors duration-300">
    Privacy Policy
  </Link>
  <Link to="/terms-and-conditions" className="hover:text-white transition-colors duration-300">
    Terms & Conditions
  </Link>
</div>

            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;